import type {
  SubCategoryItem,
  TabItem,
} from '@/components/section/hero-with-tabs/hero-tab/hero-tab';
import { getAssetIconData } from '@/lib/getImageSrc';
import contentfulClient from '@/lib/services/contentful';
import { unstable_cache } from 'next/cache';
import { cache } from 'react';
import {
  TypeProduct__category as TypeProductCategory,
  TypeProduct__categorySkeleton as TypeProductCategorySkeleton,
} from '../../types/contentful/TypeProduct__category';
import { REVALIDATION_TIME } from '../constants';

const transformProductCategoriesToTabs = (
  productCategories: TypeProductCategory<'WITHOUT_UNRESOLVABLE_LINKS', string>[],
): TabItem[] => {
  return productCategories.map((category) => ({
    slug: category.fields.slug,
    name: category.fields.name,
    description: category.fields.description || '',
    descriptionMobile: category.fields.descriptionMobile || category.fields.description || '',
    icon: getAssetIconData(category.fields.icon) ?? { src: null, alt: null },
  }));
};

const fetchProductCategoriesImpl = async (preview: boolean = false) => {
  try {
    const productCategories = await contentfulClient(
      preview,
    ).withoutUnresolvableLinks.getEntries<TypeProductCategorySkeleton>({
      content_type: 'product-category',
      include: 10,
      'fields.isActiveOnProductFilter': true,
      order: ['fields.sortOrder'],
    });

    if (!productCategories || productCategories.total === 0) {
      return [];
    }

    const categories = productCategories.items;

    return transformProductCategoriesToTabs(categories);
  } catch (error) {
    console.error('Error fetching product categories:', error);
    return [];
  }
};

export const getProductCategories = unstable_cache(
  cache(fetchProductCategoriesImpl),
  ['product-categories'],
  {
    tags: ['product-categories', 'pricing'],
    revalidate: REVALIDATION_TIME,
  },
);

const fetchSubcategoriesByParentImpl = async (
  parentCategorySlug: string,
  preview: boolean = false,
): Promise<SubCategoryItem[]> => {
  try {
    // First, get the parent category by slug to get its ID
    const parentCategory = await contentfulClient(
      preview,
    ).withoutUnresolvableLinks.getEntries<TypeProductCategorySkeleton>({
      content_type: 'product-category',
      'fields.slug': parentCategorySlug,
      'fields.isActiveOnProductFilter': true,
      limit: 1,
    });

    if (!parentCategory || parentCategory.total === 0) {
      console.log(`No parent category found with slug: ${parentCategorySlug}`);
      return [];
    }

    const parentCategoryId = parentCategory.items[0].sys.id;

    // Now get subcategories that have this parent
    const subcategories = await contentfulClient(
      preview,
    ).withoutUnresolvableLinks.getEntries<TypeProductCategorySkeleton>({
      content_type: 'product-category',
      'fields.parent.sys.id': parentCategoryId,
      'fields.isActiveOnProductFilter': false,
      include: 2,
      order: ['fields.sortOrder'],
    });

    if (!subcategories || subcategories.total === 0) {
      return [];
    }

    const subcategoryEntries = subcategories.items;

    return subcategoryEntries.map((subcategory) => ({
      slug: subcategory.fields.slug,
      name: subcategory.fields.name,
      icon: getAssetIconData(subcategory.fields.icon) ?? { src: null, alt: null },
    }));
  } catch (error) {
    console.error(`Error fetching subcategories for parent ${parentCategorySlug}:`, error);
    return [];
  }
};

export const getSubcategoriesByParent = unstable_cache(
  cache(fetchSubcategoriesByParentImpl),
  ['subcategories-by-parent'],
  {
    tags: ['subcategories', 'pricing'],
    revalidate: REVALIDATION_TIME,
  },
);
